import { useUser } from "@/composables/user/user";
import { createCollectorLink, createError404Link } from "@/router/linkFactories";
import { RouteName } from "@/router/routeNameEnum";
import { FilterSlug, parseOwnerIdParam } from "@/services/filter/filterHelpers";
import { checkUserOnInitialPageLoad } from "@/services/user/auth/login/helpers";
import { fetchUserProfile } from "@/services/user/profile/profileApi";
import { UserNotFoundException } from "@/services/user/userExceptions";
import { type NavigationGuardNext, type RouteLocationNormalized } from "vue-router";

/**
 * My items URLs carry `?ownerId=<id>` so a shared link identifies whose items it was.
 * - Missing ownerId + logged in: add own id (replace redirect).
 * - ownerId is someone else's (or visitor is logged out): redirect to that user's profile.
 * - ownerId is own: show My items as usual.
 * Must be registered before handleLogin so logged-out visitors reach the profile instead of the login.
 */
export const handleMyItemsOwner = async (toRoute: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
  if (toRoute.name !== RouteName.Collection) {
    return next();
  }

  await checkUserOnInitialPageLoad();
  const { userId } = useUser();
  const ownerId = parseOwnerIdParam(toRoute.query[FilterSlug.OwnerId]);

  if (ownerId === undefined) {
    if (userId.value === undefined) {
      return next(); // Logged out - handleLogin redirects to auth.
    }
    return next({ ...toRoute, query: { ...toRoute.query, [FilterSlug.OwnerId]: String(userId.value) }, replace: true });
  }

  if (ownerId === userId.value) {
    return next();
  }

  try {
    const profile = await fetchUserProfile({ userId: ownerId });
    return next({ ...(createCollectorLink(profile.nickname) as object), replace: true });
  } catch (e: unknown) {
    if (e instanceof UserNotFoundException) {
      return next(createError404Link());
    }
    throw e;
  }
};
